import React, { Suspense, lazy, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, Home, LayoutDashboard, LogOut, Menu, PlusCircle, Search, Sparkles, User as UserIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { LanguageToggle } from '@/components/LanguageToggle';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useAuth } from '@/hooks/use-auth';
import { useI18n } from '@/hooks/use-i18n';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';

const AuthModal = lazy(() => import('@/components/auth/AuthModal').then((m) => ({ default: m.AuthModal })));

export function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const isAuthenticated = useAuth((s) => s.isAuthenticated);
  const userName = useAuth((s) => s.user?.name);
  const userAvatar = useAuth((s) => s.user?.avatarUrl);
  const userEmail = useAuth((s) => s.user?.email);
  const userRole = useAuth((s) => s.user?.role);
  const logout = useAuth((s) => s.logout);

  const navLinks = [
    { name: 'Trang chủ', href: '/', icon: Home },
    { name: 'Khám phá', href: '/browse', icon: Search },
    { name: 'Tạo prompt', href: '/submit', icon: PlusCircle },
  ];

  if (userRole === 'admin') {
    navLinks.push({ name: 'Quản trị', href: '/admin', icon: LayoutDashboard });
  }

  const isActiveLink = (href: string) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname === href || location.pathname.startsWith(`${href}/`);
  };

  const handleNavClick = (href: string) => {
    navigate(href);
    setMobileMenuOpen(false);
  };

  const handleSearchSubmit = (value: string) => {
    const keyword = value.trim();
    if (!keyword) return;
    navigate(`/browse?q=${encodeURIComponent(keyword)}`);
    setSearchValue('');
    setMobileMenuOpen(false);
  };

  return (
    <>
      <nav className="sticky top-0 z-50 w-full border-b border-slate-200/80 bg-white/82 shadow-[0_16px_40px_-20px_rgba(148,163,184,0.45)] backdrop-blur-2xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-3 px-4 sm:h-[72px] sm:px-6 lg:px-8">
          <Link to="/" className="group flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-cyan-100 bg-gradient-to-br from-cyan-50 via-white to-teal-50 text-slate-900 shadow-[0_10px_25px_-5px_rgba(34,211,238,0.22)] transition-transform duration-200 group-hover:scale-105">
              <Sparkles className="h-5 w-5 text-cyan-500" />
            </div>
            <span className="font-display text-lg font-bold tracking-tight text-slate-900 sm:text-xl">
              Prompt<span className="text-cyan-500">.</span>
            </span>
          </Link>

          <div className="hidden flex-1 justify-center lg:flex">
            <div className="flex items-center gap-1 rounded-full border border-slate-200 bg-white/92 p-1.5 shadow-[0_14px_40px_-18px_rgba(148,163,184,0.42)] backdrop-blur-xl">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={cn(
                    'inline-flex min-h-12 items-center gap-2 rounded-full px-5 py-2.5 text-[15px] font-medium transition-all duration-200',
                    isActiveLink(link.href)
                      ? 'bg-[linear-gradient(135deg,rgba(20,184,166,0.14),rgba(34,211,238,0.18))] text-slate-900 ring-1 ring-cyan-200 shadow-[0_8px_22px_-16px_rgba(34,211,238,0.5)]'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  )}
                >
                  <link.icon className={cn('h-4.5 w-4.5', isActiveLink(link.href) ? 'text-cyan-600' : 'text-slate-500')} />
                  {link.name}
                </Link>
              ))}
            </div>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <div className="hidden items-center gap-2 md:flex">
              <LanguageToggle />
              <ThemeToggle className="shrink-0" />
            </div>

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="h-11 rounded-full border border-slate-200 bg-white px-2.5 text-slate-700 shadow-[0_10px_25px_-12px_rgba(148,163,184,0.5)] hover:bg-slate-50"
                  >
                    <Avatar className="h-7 w-7">
                      <AvatarImage src={userAvatar ?? undefined} alt={userName ?? 'Người dùng'} />
                      <AvatarFallback className="bg-slate-100 text-xs text-slate-700">{userName?.charAt(0) ?? 'U'}</AvatarFallback>
                    </Avatar>
                    <span className="hidden max-w-24 truncate pl-2 text-[15px] font-medium sm:inline">{userName}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" forceMount className="w-64 border-slate-200 bg-white text-slate-900 shadow-[0_24px_80px_-28px_rgba(148,163,184,0.5)] backdrop-blur-2xl">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-semibold leading-none text-slate-900">{userName}</p>
                      <p className="text-xs leading-none text-slate-500">{userEmail}</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-slate-200" />
                  <DropdownMenuItem className="cursor-pointer text-slate-600 focus:bg-cyan-50 focus:text-slate-900" onClick={() => navigate('/profile')}>
                    <UserIcon className="mr-2 h-4 w-4" />
                    {t('nav.profile')}
                  </DropdownMenuItem>
                  {userRole === 'admin' ? (
                    <DropdownMenuItem className="cursor-pointer text-slate-600 focus:bg-cyan-50 focus:text-slate-900" onClick={() => navigate('/admin')}>
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      Quản trị
                    </DropdownMenuItem>
                  ) : null}
                  <DropdownMenuSeparator className="bg-slate-200" />
                  <DropdownMenuItem className="cursor-pointer text-slate-600 focus:bg-cyan-50 focus:text-slate-900" onClick={() => logout()}>
                    <LogOut className="mr-2 h-4 w-4" />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button variant="ghost" onClick={() => setIsAuthModalOpen(true)} className="h-11 rounded-full border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50">
                {t('nav.signIn')}
              </Button>
            )}

            <div className="md:hidden">
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-11 w-11 rounded-full border border-slate-200 bg-white text-slate-700 hover:bg-slate-50" aria-label="Mở menu">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="!w-screen !max-w-none border-l border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_60%,#f1f5f9_100%)] p-0 text-slate-900 shadow-[0_30px_120px_-48px_rgba(148,163,184,0.7)]">
                  <div className="relative flex h-full flex-col overflow-hidden">
                    <div className="pointer-events-none absolute inset-0">
                      <div className="absolute -right-24 top-16 h-64 w-64 rounded-full bg-cyan-200/40 blur-[120px]" />
                      <div className="absolute -left-20 bottom-10 h-72 w-72 rounded-full bg-teal-100/50 blur-[140px]" />
                    </div>

                    <div className="relative flex items-center justify-between border-b border-slate-200 px-5 py-5">
                      <Link to="/" className="flex items-center gap-3" onClick={() => setMobileMenuOpen(false)}>
                        <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-cyan-100 bg-gradient-to-br from-cyan-50 to-teal-50 shadow-[0_10px_25px_-5px_rgba(34,211,238,0.2)]">
                          <Sparkles className="h-5 w-5 text-cyan-500" />
                        </div>
                        <div className="text-lg font-bold tracking-tight text-slate-900">
                          Prompt<span className="text-cyan-500">.</span>
                        </div>
                      </Link>
                    </div>

                    <div className="relative flex-1 overflow-y-auto px-5 py-6">
                      <SheetHeader className="space-y-3 text-left">
                        <SheetTitle className="font-display text-3xl font-bold tracking-tight text-slate-900">Khám phá Prompt</SheetTitle>
                        <SheetDescription className="max-w-sm text-sm leading-6 text-slate-600">
                          Tìm prompt nhanh, tạo mới trong một chạm, hoặc vào tài khoản để lưu lịch sử và mục yêu thích.
                        </SheetDescription>
                      </SheetHeader>

                      <form
                        className="mt-6 space-y-3"
                        onSubmit={(e) => {
                          e.preventDefault();
                          handleSearchSubmit(searchValue);
                        }}
                      >
                        <div className="relative">
                          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                          <Input
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            type="search"
                            placeholder={t('nav.searchPlaceholder')}
                            className="h-12 rounded-2xl border-slate-200 bg-white pl-11 pr-4 text-slate-900 placeholder:text-slate-400 shadow-none focus-visible:border-cyan-300 focus-visible:ring-2 focus-visible:ring-cyan-100"
                          />
                        </div>
                        <Button type="submit" className="h-12 w-full rounded-2xl bg-gradient-to-r from-[#14B8A6] to-[#22D3EE] text-white shadow-[0_12px_30px_rgba(34,211,238,0.2)]">
                          <ArrowRight className="h-4 w-4" />
                          Tìm kiếm
                        </Button>
                      </form>

                      <div className="mt-8 space-y-3">
                        {navLinks.map((link) => (
                          <Button
                            key={link.href}
                            variant="ghost"
                            className={cn(
                              'h-14 w-full justify-between rounded-2xl border px-[18px] text-left text-base font-medium transition-all',
                              isActiveLink(link.href)
                                ? 'border-cyan-200 bg-cyan-50 text-slate-900 shadow-[0_10px_25px_-12px_rgba(34,211,238,0.25)]'
                                : 'border-slate-200 bg-white text-slate-600 hover:border-cyan-200 hover:bg-cyan-50/60 hover:text-slate-900'
                            )}
                            onClick={() => handleNavClick(link.href)}
                          >
                            <span className="flex items-center gap-3">
                              <link.icon className="h-4 w-4" />
                              {link.name}
                            </span>
                            <ArrowRight className="h-4 w-4 text-cyan-500" />
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </nav>

      <Suspense fallback={null}>
        <AuthModal isOpen={isAuthModalOpen} onOpenChange={setIsAuthModalOpen} />
      </Suspense>
    </>
  );
}
